import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { droneShots, droneSection, dronePager, PREVIEW_COUNT } from './data/drones';
import useInView from '../hooks/useInView';
import ShotImage from './ShotImage';
import ShotMeta from './ShotMeta';
import Lightbox from './Lightbox';
import { pagerItems } from '../utils/pager';

const SPANS = { wide: 'shot-wide', tall: 'shot-tall' };

// Same origin as the site in production. Override at build time when the API is
// deployed separately.
const API = (process.env.REACT_APP_DRONE_API || '').replace(/\/$/, '');

// The static array is the fallback, so the landing page still renders with no API.
const fallbackShots = droneShots.slice(0, PREVIEW_COUNT);

const FALLBACK_VIEW = {
  live: false,
  shots: fallbackShots,
  page: 1,
  pageCount: 1,
  total: 0,
  offset: 0,
};

const fromApi = (p) => ({
  src: `${API}/photos/${p.file}`,
  title: p.title || null,
  location: p.location || null,
  taken: p.taken || null,
  camera: p.camera || null,
  coords: p.coords || null,
  span: p.span || 'normal',
});

const fill = (template, values) =>
  Object.keys(values).reduce((text, key) => text.replace(`{${key}}`, values[key]), template);

const DroneGallery = () => {
  const [ref, inView] = useInView();
  const [failed, setFailed] = useState(() => new Set());
  const [openIndex, setOpenIndex] = useState(null);
  const [view, setView] = useState(FALLBACK_VIEW);
  // A fresh object per click, so retrying a page that just failed still refetches.
  const [request, setRequest] = useState({ page: 1 });
  const [loading, setLoading] = useState(Boolean(API));
  const [notice, setNotice] = useState('');
  const headingRef = useRef(null);
  const wantFocus = useRef(false);
  const generation = useRef(0); // id of the latest page request
  const liveRef = useRef(false); // whether a live page is on screen, for the error path
  const shots = view.shots;

  // The landing page shows one compact page of three at a time, so a photo added in the studio
  // appears here without a rebuild and the section never grows. Falls back to the
  // static array on any failure before a live page has loaded.
  useEffect(() => {
    if (!API) return undefined;
    const controller = new AbortController();
    // Aborting alone is not enough: a response that already resolved, or whose json() is
    // still pending, can land after a newer navigation. Only the latest request may write.
    const id = ++generation.current;
    const isCurrent = () => id === generation.current && !controller.signal.aborted;

    const fetchPage = async (n) => {
      const qs = new URLSearchParams({
        limit: String(PREVIEW_COUNT),
        offset: String((n - 1) * PREVIEW_COUNT),
      });
      const res = await fetch(`${API}/feed.json?${qs}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`feed ${res.status}`);
      return res.json();
    };

    const run = async () => {
      if (!isCurrent()) return;
      setLoading(true);
      try {
        let at = request.page;
        let data = await fetchPage(at);
        // An out-of-range page (rows deleted since the last load) comes back empty while
        // the total says there are photos. Fetch the real last page once, never a loop.
        if (isCurrent() && (data.photos || []).length === 0 && data.total > 0) {
          at = Math.max(1, Math.ceil(data.total / PREVIEW_COUNT));
          data = await fetchPage(at);
        }
        if (!isCurrent()) return;
        const rows = (data.photos || []).map(fromApi);
        setNotice('');
        liveRef.current = rows.length > 0;
        if (rows.length === 0) {
          setView(FALLBACK_VIEW);
        } else {
          setView({
            live: true,
            shots: rows,
            page: at,
            pageCount: data.pageCount ?? Math.ceil(data.total / PREVIEW_COUNT),
            total: data.total ?? rows.length,
            offset: data.offset ?? (at - 1) * PREVIEW_COUNT,
          });
        }
        setOpenIndex(null);
        setLoading(false);
      } catch (err) {
        // A superseded or aborted request is not a failure.
        if (!isCurrent() || (err && err.name === 'AbortError')) return;
        // Before any live page, the static fallback already on screen stays. After one,
        // keep the photos shown and say the new page did not load, rather than swap in
        // unrelated placeholders.
        if (liveRef.current) setNotice(dronePager.loadError);
        setLoading(false);
      }
    };

    run();
    return () => controller.abort();
  }, [request]);

  // After a page change, move focus to the section heading once the new photos are in.
  useEffect(() => {
    if (!wantFocus.current || loading) return;
    wantFocus.current = false;
    const node = headingRef.current;
    if (!node) return;
    node.focus({ preventScroll: true });
    if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'start' });
  }, [view, notice, loading]);

  const goTo = (page) => {
    if (page === view.page) return;
    wantFocus.current = true;
    setRequest({ page });
  };

  const markFailed = useCallback((src) => {
    setFailed((current) => (current.has(src) ? current : new Set(current).add(src)));
  }, []);

  return (
    <section
      id="drones"
      aria-labelledby="drones-title"
      ref={ref}
      className={`section drones reveal${inView ? ' in-view' : ''}`}
    >
      <div className="container">
        <div className="section-head">
          <h2 id="drones-title" className="section-title" tabIndex={-1} ref={headingRef}>
            {droneSection.title}
          </h2>
          <p className="section-intro">
            {droneSection.intro}
            {droneSection.viewAllHref ? (
              <>
                {' '}
                <a href={droneSection.viewAllHref} target="_blank" rel="noopener noreferrer">
                  View all
                </a>
              </>
            ) : null}
          </p>
          {/* The landing page shows a short preview. Everything lives on /gallery. */}
          <Link to="/gallery" className="section-more">
            {droneSection.moreLabel}
            <span className="nav-ext" aria-hidden="true">
              {' '}
              {'\u2197'}
            </span>
          </Link>
        </div>

        <ul className="shot-grid" aria-busy={loading}>
          {shots.map((shot, index) => (
            <li className={`shot ${SPANS[shot.span] || ''}`} key={shot.src}>
              <figure>
                <button
                  type="button"
                  className="shot-open"
                  aria-haspopup="dialog"
                  aria-label={`Open photo ${index + 1} of ${shots.length}: ${
                    shot.title || 'Drone photograph'
                  }`}
                  onClick={() => setOpenIndex(index)}
                >
                  <ShotImage
                    shot={shot}
                    failed={failed.has(shot.src)}
                    onFail={markFailed}
                    className="shot-img"
                  />
                </button>
                <figcaption>
                  <p className="shot-title">
                    {shot.title || 'Drone photograph'}
                    {shot.location ? <span className="shot-place">{shot.location}</span> : null}
                  </p>
                  <ShotMeta shot={shot} />
                </figcaption>
              </figure>
            </li>
          ))}
        </ul>

        {view.live && view.pageCount > 1 && (
          <nav className="shot-pager" aria-label={dronePager.label}>
            <ul className="shot-pager-pages">
              <li>
                <button
                  type="button"
                  className="pager-link"
                  aria-label={dronePager.prevLabel}
                  disabled={view.page <= 1}
                  onClick={() => goTo(view.page - 1)}
                >
                  {dronePager.prev}
                </button>
              </li>
              {pagerItems(view.page, view.pageCount).map((item) => (
                <li key={item.gap || item.n}>
                  {item.gap ? (
                    <span className="pager-gap" aria-hidden="true">
                      …
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="pager-link"
                      aria-label={fill(dronePager.pageLinkLabel, { page: item.n })}
                      aria-current={item.n === view.page ? 'page' : undefined}
                      onClick={() => goTo(item.n)}
                    >
                      {item.n}
                    </button>
                  )}
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="pager-link"
                  aria-label={dronePager.nextLabel}
                  disabled={view.page >= view.pageCount}
                  onClick={() => goTo(view.page + 1)}
                >
                  {dronePager.next}
                </button>
              </li>
            </ul>
            <p className="shot-pager-count" role="status">
              {fill(dronePager.range, {
                from: view.offset + 1,
                to: view.offset + shots.length,
                total: view.total,
              })}
              <span className="shot-pager-page">
                {fill(dronePager.pageOf, { page: view.page, pages: view.pageCount })}
              </span>
              {notice ? <span className="shot-pager-note">{notice}</span> : null}
            </p>
          </nav>
        )}
      </div>

      {openIndex !== null && (
        <Lightbox
          shots={shots}
          index={openIndex}
          failed={failed}
          onFail={markFailed}
          onIndex={setOpenIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </section>
  );
};

export default DroneGallery;
